import React from 'react';
import { Button } from "@mui/material";
import { AddRounded } from "@mui-symbols-material/w300";
import { useTranslation } from "react-i18next";
import { InstalledBaseNode } from "../../codegen/graphql";
import { useAllowedChildNodeTypes } from "../../hooks/useAllowedChildNodeTypes";
import {
    GuidanceAction,
    getGuidanceActionLabel,
    resolveEmptyColumnGuidance,
} from "../../utils/emptyColumnGuidance";
import { canAddAnyChild, canOfferChildCreation } from "../../utils/nodeChildRules";

interface Props {
    parentNode: InstalledBaseNode;
    onCreate: (action: GuidanceAction) => void;
}

/**
 * Single create button for a nameplate column (an empty column under a SYSTEM node, where
 * the nameplate replaces the guided empty state). Names the one obvious thing to add —
 * "Create Pressure line" for a system whose rules whitelist PRESSURE_LINE — by reusing the
 * empty state's resolver, so the two can't disagree.
 *
 * Lives in its own component so `InstalledBaseNodeTypeList` is only queried for columns
 * that can actually show a create affordance, rather than for every rendered column.
 */
const NameplateCreateAction = ({ parentNode, onCreate }: Props) => {
    const { t } = useTranslation();
    const { creatable, loading } = useAllowedChildNodeTypes(parentNode);

    const allowed =
        canOfferChildCreation(false, parentNode?.nodeRules, !!parentNode?.access?.write) &&
        canAddAnyChild(parentNode?.nodeRules);

    // No spinner: the nameplate above is already the column's content, so the button just
    // appears once the type catalogue is in.
    if (loading || !allowed) return null;

    const guidance = resolveEmptyColumnGuidance(creatable);
    if (guidance.variant === 'none') return null;

    const action = guidance.primary;

    return (
        <Button
            variant="outlined"
            fullWidth
            startIcon={<AddRounded fontSize={'small'} />}
            sx={{ mt: '8px' }}
            onClick={() => onCreate(action)}
        >
            {getGuidanceActionLabel(action, t, parentNode?.nodeType?.key)}
        </Button>
    );
};

export default NameplateCreateAction;
